import type { ServiceStandardByIdResult } from "@/modules/service-standard/domain/types";
import { formatDateLocal, formatDateTimeLocal } from "@/shared/lib/date-time";

interface ServiceStandardPdfPreviewContentProps {
  standard: ServiceStandardByIdResult;
}

const serviceStageLabels: Record<string, string> = {
  before: "ກ່ອນບໍລິການ",
  during: "ລະຫວ່າງບໍລິການ",
  after: "ຫຼັງບໍລິການ",
  all: "ທຸກເວລາ",
};

const statusLabels: Record<string, string> = {
  active: "ໃຊ້ງານ",
  draft: "ປັບປຸງ",
  inactive: "ເລີກໃຊ້",
};

export function ServiceStandardPdfPreviewContent({
  standard,
}: ServiceStandardPdfPreviewContentProps) {
  const doItems =
    standard?.items?.filter((item) => item.itemType === "do") ?? [];
  const dontItems =
    standard?.items?.filter((item) => item.itemType === "dont") ?? [];

  return (
    <div
      id="service-standard-pdf-content"
      className="mx-auto max-w-4xl bg-white p-8 font-serif text-gray-900 leading-relaxed print:max-w-none print:p-0 print:font-serif print:text-gray-900 print:leading-relaxed"
      style={{
        fontFamily: '"Times New Roman", Times, serif',
        lineHeight: "1.5",
        color: "#1a1a1a",
      }}
    >
      {/* PDF Header - Letterhead Style */}
      <div className="mb-8 border-gray-800 border-b-4 pb-6 print:mb-6">
        <div className="mb-6 text-center">
          <h1 className="font-bold text-4xl text-gray-800 uppercase tracking-wider">
            FREEDOM Sole Co.Ltd
          </h1>
          <p className="mt-2 text-gray-600 text-lg">ມາດຕະຖານບໍລິການ</p>
          <div className="mx-auto mt-4 h-1 w-24 bg-gray-800" />
        </div>

        <div className="flex items-start justify-between">
          <div className="text-sm">
            <p className="font-semibold">
              ເອກະສານເລກທີ:{" "}
              <span className="font-normal">{`SS-${standard?.id.slice(-6).toUpperCase()}`}</span>
            </p>
            <p className="mt-1 font-semibold">
              ສະບັບທີ: <span className="font-normal">1.0</span>
            </p>
          </div>
          <div className="text-right text-sm">
            <p className="font-semibold">ວັນທີອັບເດດລ່າສຸດ:</p>
            <p className="font-normal">
              {formatDateLocal(standard?.updatedAt ?? new Date())}
            </p>
            <p className="mt-2 font-semibold">ວັນທີພິມ:</p>
            <p className="font-normal">{formatDateLocal(new Date())}</p>
          </div>
        </div>
      </div>

      {/* Service Standard Title */}
      <div className="mb-12 text-center">
        <h2 className="mb-6 border-gray-300 border-b-2 pb-4 font-bold text-3xl text-gray-800 uppercase tracking-wide">
          {standard?.name}
        </h2>
      </div>

      {/* Section 1: ຂໍ້ມູນພື້ນຖານ (Basic Information) */}
      <div className="mb-10">
        <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
          1. ຂໍ້ມູນພື້ນຖານ
        </h3>

        <table className="mb-6 w-full border-collapse border-2 border-gray-400">
          <tbody>
            <tr className="border-gray-300 border-b">
              <td className="w-1/3 border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຊື່ມາດຕະຖານ
              </td>
              <td className="p-4 text-sm leading-relaxed">{standard?.name}</td>
            </tr>
            {standard?.description && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ຄຳອະທິບາຍ
                </td>
                <td className="p-4 text-sm leading-relaxed">
                  {standard?.description}
                </td>
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ຊ່ອງທາງບໍລິການ
              </td>
              <td className="p-4 text-sm">
                {standard?.serviceChannelName || "ບໍ່ໄດ້ກໍານົດ"}
              </td>
            </tr>
            {standard?.departmentName && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ຜະແນກ
                </td>
                <td className="p-4 text-sm">{standard?.departmentName}</td>
              </tr>
            )}
            {standard?.positionTitle && (
              <tr className="border-gray-300 border-b">
                <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                  ຕໍາແໜ່ງ
                </td>
                <td className="p-4 text-sm">{standard?.positionTitle}</td>
              </tr>
            )}
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ໃຊ້ເວລາໃດ
              </td>
              <td className="p-4 text-sm">
                {serviceStageLabels[standard?.serviceStage ?? ""] ||
                  standard?.serviceStage}
              </td>
            </tr>
            <tr className="border-gray-300 border-b">
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ສະຖານະ
              </td>
              <td className="p-4 text-sm">
                {statusLabels[standard?.status ?? ""] || standard?.status}
              </td>
            </tr>
            <tr>
              <td className="border-gray-300 border-r bg-gray-100 p-4 font-semibold text-gray-700">
                ວັນທີອັບເດດລ່າສຸດ
              </td>
              <td className="p-4 text-sm">
                {formatDateTimeLocal(standard?.updatedAt ?? new Date())}
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Section 2: ສິ່ງທີ່ຕ້ອງເຮັດ (Do Items) */}
      {doItems.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            2. ສິ່ງທີ່ຕ້ອງເຮັດ
          </h3>

          <div className="space-y-4">
            {doItems.map((item, index) => (
              <div className="mb-2 flex items-start gap-3" key={item.id}>
                <h4 className="mt-1 flex shrink-0 items-center justify-center rounded-full">
                  2.{index + 1}
                </h4>
                <div className="flex-1">
                  <p className="font-semibold text-base text-gray-800">
                    {item.content}
                  </p>
                  {item.description && (
                    <p className="mt-2 text-gray-700 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 3: ສິ່ງທີ່ຫ້າມເຮັດ (Don't Items) */}
      {dontItems.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            3. ສິ່ງທີ່ຫ້າມເຮັດ
          </h3>

          <div className="space-y-4">
            {dontItems.map((item, index) => (
              <div key={item.id} className="mb-2 flex items-start gap-3">
                <h4 className="mt-1 flex shrink-0 items-center justify-center rounded-full">
                  3.{index + 1}
                </h4>
                <div className="flex-1">
                  <p className="font-semibold text-base text-gray-800">
                    {item.content}
                  </p>
                  {item.description && (
                    <p className="mt-2 text-gray-700 text-sm leading-relaxed">
                      {item.description}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 4: ຕົວຢ່າງປະໂຫຍກ (Examples) */}
      {standard?.examples && standard?.examples.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            4. ຕົວຢ່າງປະໂຫຍກ
          </h3>

          <div className="space-y-4">
            {standard?.examples.map((example, index) => (
              <div key={example.id}>
                <h4 className="mb-2 font-semibold">
                  4.{index + 1}. {example.situation}
                </h4>
                <p className="mb-1 text-gray-800 text-sm leading-relaxed">
                  {example.example}
                </p>
                {example.description && (
                  <p className="mt-2 text-gray-600 text-xs">
                    {example.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Section 5: ກໍລະນີພິເສດ (Special Cases) */}
      {standard?.specialCases && standard?.specialCases.length > 0 && (
        <div className="mb-10">
          <h3 className="mb-6 border-gray-300 border-b-2 pb-2 font-bold text-gray-800 text-xl uppercase tracking-wide">
            5. ກໍລະນີພິເສດ
          </h3>

          <div className="space-y-4">
            {standard?.specialCases.map((specialCase, index) => (
              <div key={specialCase.id}>
                <h4 className="mb-2 font-semibold">
                  5.{index + 1}. {specialCase.title}
                </h4>
                <p className="mb-1 text-gray-800 text-sm leading-relaxed">
                  {specialCase.handling}
                </p>
                {specialCase.description && (
                  <p className="mt-2 text-gray-600 text-xs">
                    {specialCase.description}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer - Signatures and Approval */}
      <div className="mt-16 border-gray-800 border-t-4 pt-8">
        <div className="grid grid-cols-2 gap-8 text-sm">
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານອະນຸມັດໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
          <div>
            <p className="mb-2 font-bold text-gray-800 uppercase tracking-wide">
              ເອກະສານທົບທວນໂດຍ
            </p>
            <div className="mb-2 border-gray-400 border-b pb-8" />
            <p className="text-gray-600 text-xs">ຊື່-ນາມສະກຸນ, ຕໍາແໜ່ງ</p>
            <p className="text-gray-600 text-xs">ວັນທີ: _______________</p>
          </div>
        </div>

        <div className="mt-8 border-gray-300 border-t pt-6 text-center text-gray-500 text-xs">
          <p className="font-medium">
            ເອກະສານນີ້ໄດ້ຮັບການສ້າງຈາກລະບົບຈັດການມາດຕະຖານບໍລິການ - FREEDOM Sole Co.Ltd
          </p>
          <p className="mt-2">
            ເລກເອກະສານ: {`SS-${standard?.id.slice(-6).toUpperCase()}`} | ພິມເມື່ອ:{" "}
            {formatDateLocal(new Date())}
          </p>
        </div>
      </div>
    </div>
  );
}
